import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableContainer, TableHead,IconButton, TableRow, CircularProgress, Tooltip, Grid2, Box, } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StatusChip from '../../customTableComponent/statusChip';
import PackageStatusChip from '../../../pages/Billing/Package/StatusChip';
import { ReactComponent as EditButton } from '../../../assets/SVG/editPencil.svg';
import { ReactComponent as ViewIcon } from '../../../assets/SVG/eye_view.svg';
import { ReactComponent as PrinterIcon } from '../../../assets/SVG/printer (1).svg';
import { getHysteroscopy, getHysteroscopyList, HysteroscopyType } from './hysteroscopy.helper';
import PrescribedUnavailable from '../prescribedUnavailable';
import { usePatientContext } from '../../SubPatinetProvider';
import { ProcedureSummary } from '../laparoscopy/laparoscopy.helper';
import ReusableReportComponent from '../../printReports/ReusableReportComponent';
import HysteroscopyReport from './HysteroscopyReport';


const HysteroscopyList = ({
  onEdit,
  onView,
  canEditOrAdd,
  canView
}: {
  onEdit: (id: number) => void;
  onView: (id: number) => void;
  canEditOrAdd: boolean;
  canView: boolean;
}) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [laparoscopyDetails, setLaparoscopyDetails] = useState<any[]>([]);
  const [procedureSummary, setProcedureSummary] = useState<ProcedureSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPrint, setShowPrint] = useState(false);
  const [reportData, setReportData] = useState<HysteroscopyType | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState<'completed' | 'pending' | null>(null);

  const { patientId } = usePatientContext();

  const handleCloseReport = () => {
    setShowPrint(false);
    setReportData(null);
  };

  const handlePrintReport = async (id: number) => {
    try {
      const response = await getHysteroscopy(id);
      setReportData(response);
      setShowPrint(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching hysteroscopy report data:', error);
    }
  };
  
  const fetchHysteroscopyList = useCallback((status?: 'completed' | 'pending') => {
    setLoading(true);
    getHysteroscopyList(patientId, status)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        setLaparoscopyDetails(response.objects);
        setProcedureSummary(response.procedure_summary);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching laparoscopy details:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [patientId]);

  useEffect(() => {
    fetchHysteroscopyList();
  }, [fetchHysteroscopyList]);

  const handleStatusFilterClick = (status: 'completed' | 'pending') => {
    setActiveStatusFilter(status);
    fetchHysteroscopyList(status);
  };

  const handleResetStatusFilter = () => {
    setActiveStatusFilter(null);
    fetchHysteroscopyList();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  
  if (!laparoscopyDetails.length && !activeStatusFilter) {
    return <PrescribedUnavailable message={tSurgery('hysteroscopyNotPrescribed', { defaultValue: 'No hysteroscopy has been prescribed yet' })} />;
  }
  
  
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ height: '2.6rem', background: 'rgba(250, 250, 250, 1)', '& .MuiTableCell-root': { padding: '0.7rem 0.7.5rem', }, }} >
              {[tSurgery('dateOfProcedure', { defaultValue: 'Date of Procedure' }), tSurgery('procedureId', { defaultValue: 'Procedure ID' }), tSurgery('surgeonName', { defaultValue: 'Surgeon Name' }), tGeneral('status', { defaultValue: 'Status' }), ''].map(
                (title, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.45',
                      textAlign: 'left',
                      color: 'rgba(35, 35, 35, 1)',
                    }}
                  >
                    {title}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody sx={{
            '& .MuiTableCell-root': {
              textAlign: 'left',
              padding: '0.9rem 0.75rem',
              borderBottom: '1px solid #EEF2F6',
            },
          }}>
            {laparoscopyDetails.map((laparoscopy) => (
              <TableRow key={laparoscopy.id}>
                <TableCell>
                  {new Date(laparoscopy.created_on).toLocaleDateString('en-US')}
                </TableCell>
                <TableCell>{laparoscopy?.services[0]?.code}</TableCell>
                <TableCell>{laparoscopy.doctor_name}</TableCell>
                <TableCell>
                  <StatusChip value={laparoscopy.hysteroscopy_status ? 'Completed' : 'Pending'} />
                </TableCell>
                <TableCell>
                  {laparoscopy.hysteroscopy_status && canView ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={tGeneral('viewDetails', { defaultValue: 'View Details' })}>
                        <IconButton aria-label={tGeneral('viewDetails', { defaultValue: 'View Details' })} onClick={() => onView(laparoscopy.id)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tGeneral('print_Print', { defaultValue: 'Print Report' })}>
                        <IconButton onClick={() => handlePrintReport(laparoscopy.id)}>
                          <PrinterIcon  />
                        </IconButton>
                      </Tooltip> 
                    </Box>
                    
                  ) :
                    (!laparoscopy.hysteroscopy_status && canEditOrAdd) ? (
                      <Tooltip title={tGeneral('editDetails', { defaultValue: 'Edit Details' })}>
                        <IconButton aria-label={tGeneral('editDetails', { defaultValue: 'Edit Details' })} onClick={() => onEdit(laparoscopy.id)} >
                          <EditButton />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {procedureSummary?.hysteroscopy && (
        <Box sx={{ position: 'fixed', bottom: 0, left: 300, right: 0, px: 4, py: 2, pb: 4, bgcolor: '#fff', }} >
          <Grid2 container justifyContent="flex-end" alignItems="center" spacing={2}>
            <Grid2>
              <PackageStatusChip
                name="completed"
                label={tGeneral('completed', { defaultValue: 'Completed' })}
                count={procedureSummary.hysteroscopy.completed ?? 0}
                selected={activeStatusFilter === 'completed'}
                onClick={() => handleStatusFilterClick('completed')}
                height={34}
              />
            </Grid2>
            <Grid2>
              <PackageStatusChip
                name="InProgress"
                label={tGeneral('pending', { defaultValue: 'Pending' })}
                count={procedureSummary.hysteroscopy.pending ?? 0}
                selected={activeStatusFilter === 'pending'}
                onClick={() => handleStatusFilterClick('pending')}
                height={34}
              />
            </Grid2>

            {activeStatusFilter && (
              <Grid2>
                <Tooltip title={tGeneral('reset', { defaultValue: 'Reset' })}>
                  <IconButton onClick={handleResetStatusFilter}>
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}

      {!laparoscopyDetails.length && activeStatusFilter && (
        <Box sx={{ py: 3 }}>
          <PrescribedUnavailable
            message={tGeneral('noDataFound', { defaultValue: 'No records found for selected status' })}
          />
        </Box>
      )}

      <ReusableReportComponent
        open={showPrint}
        onClose={handleCloseReport}
        reportTitle={tSurgery('hysteroscopyReportTitle', { defaultValue: 'Hysteroscopy Report' })}
      >
        {reportData && <HysteroscopyReport data={reportData} />}
      </ReusableReportComponent>
    </>
  );
};
export default HysteroscopyList;
